/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronRight, ChevronDown, Edit2, Trash2,
  Plus, Search, Eye, EyeOff, ListTree, AlertTriangle, RotateCcw
} from 'lucide-react';
import Modal from '../Modal';
import { serviceRequestTypeService } from '../../services/serviceRequestTypeService';
import toastService from '../../utils/toastService';
import { SERVICE_REQUEST_LABEL } from '../../constants/entityLabels';

const LEVEL_COLOR_PALETTES = [
  { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/20', dot: 'bg-emerald-500' },
  { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/20', dot: 'bg-blue-500' },
  { bg: 'bg-purple-500/10', text: 'text-purple-600', border: 'border-purple-500/20', dot: 'bg-purple-500' },
  { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/20', dot: 'bg-amber-500' },
  { bg: 'bg-rose-500/10', text: 'text-rose-600', border: 'border-rose-500/20', dot: 'bg-rose-500' },
];

const TreeNodeItem = ({
  node,
  levels,
  onQuickAddChild,
  onEditNode,
  onDeleteNode,
  onReactivateNode,
  isParentActive = true,
  collapsedMap,
  toggleCollapse,
  searchQuery,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isCollapsed = !!collapsedMap[node.uid];
  const depth = node.depth || 0;
  const levelInfo = levels[depth] || { name: `Level ${depth + 1}`, key: `level_${depth}` };
  const palette = LEVEL_COLOR_PALETTES[depth % LEVEL_COLOR_PALETTES.length];
  const isLastLevel = depth >= levels.length - 1;

  const matchesSearch =
    searchQuery && node.name.toLowerCase().includes(searchQuery.toLowerCase());

  return (
    <div className="space-y-1">
      <div
        className={`group flex items-center justify-between gap-2 p-2 sm:px-3 sm:py-2 rounded-xl border transition-all ${
          matchesSearch
            ? 'bg-primary/10 border-primary shadow-sm'
            : !node.isActive
            ? 'bg-base-200/30 border-base-200 opacity-60'
            : 'bg-base-100 hover:bg-base-200/60 border-base-content/10'
        }`}
        style={{ marginLeft: `${Math.min(depth * 18, 120)}px` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggleCollapse(node.uid)}
              className="p-1 text-base-content/50 hover:text-base-content rounded-md hover:bg-base-200 transition-colors"
            >
              {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            </button>
          ) : (
            <span className="w-5 flex items-center justify-center text-base-content/20">
              •
            </span>
          )}

          {/* Level Badge */}
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border shrink-0 ${palette.bg} ${palette.text} ${palette.border}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${palette.dot}`}></span>
            <span>{levelInfo.name}</span>
          </span>

          {/* Node Name */}
          <span className={`text-xs font-bold truncate ${!node.isActive ? 'line-through text-base-content/50' : 'text-base-content'}`}>
            {node.name}
          </span>

          {hasChildren && (
            <span className="text-[10px] font-medium text-base-content/40 shrink-0">
              ({node.children.length})
            </span>
          )}

          {!node.isActive && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-base-300 text-base-content/50 font-bold uppercase tracking-wider">
              Inactive
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
          {!isLastLevel && node.isActive && (
            <button
              type="button"
              title={`Add ${levels[depth + 1]?.name || 'child'} under ${node.name}`}
              onClick={() => onQuickAddChild(node)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/10 hover:bg-primary text-primary hover:text-primary-content text-[11px] font-bold transition-all shadow-xs"
            >
              <Plus size={12} />
              <span className="hidden sm:inline">Add {levels[depth + 1]?.name}</span>
            </button>
          )}

          <button
            type="button"
            title="Edit Option"
            onClick={() => onEditNode(node)}
            className="p-1.5 text-base-content/50 hover:text-base-content hover:bg-base-200 rounded-lg transition-colors"
          >
            <Edit2 size={13} />
          </button>

          {node.isActive ? (
            <button
              type="button"
              title="Deactivate Option"
              onClick={() => onDeleteNode(node)}
              className="p-1.5 text-base-content/50 hover:text-error hover:bg-base-200 rounded-lg transition-colors"
            >
              <Trash2 size={13} />
            </button>
          ) : isParentActive ? (
            <button
              type="button"
              title="Reactivate Option"
              onClick={() => onReactivateNode(node)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/10 hover:bg-emerald-500 text-emerald-600 hover:text-white text-[11px] font-bold transition-all shadow-xs cursor-pointer"
            >
              <RotateCcw size={12} />
              <span className="hidden sm:inline">Reactivate</span>
            </button>
          ) : (
            <button
              type="button"
              disabled
              title="Cannot reactivate: Parent option is inactive. Reactivate parent first."
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-base-300/40 text-base-content/30 text-[11px] font-bold border border-base-content/10 cursor-not-allowed opacity-50 select-none"
            >
              <RotateCcw size={12} />
              <span className="hidden sm:inline">Reactivate</span>
            </button>
          )}
        </div>
      </div>

      {/* Render Children Recursively */}
      {hasChildren && !isCollapsed && (
        <div className="space-y-1 relative pl-1 border-l border-base-content/10 ml-3">
          {node.children.map((child) => (
            <TreeNodeItem
              key={child.uid}
              node={child}
              isParentActive={isParentActive && node.isActive}
              levels={levels}
              onQuickAddChild={onQuickAddChild}
              onEditNode={onEditNode}
              onDeleteNode={onDeleteNode}
              onReactivateNode={onReactivateNode}
              collapsedMap={collapsedMap}
              toggleCollapse={toggleCollapse}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
};

TreeNodeItem.propTypes = {
  node: PropTypes.object.isRequired,
  levels: PropTypes.array.isRequired,
  onQuickAddChild: PropTypes.func.isRequired,
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired,
  onReactivateNode: PropTypes.func.isRequired,
  isParentActive: PropTypes.bool,
  collapsedMap: PropTypes.object.isRequired,
  toggleCollapse: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
};

const HierarchyTreeView = ({
  typeUid,
  levels = [],
  tree = [],
  nodes = [],
  onRefresh,
  onQuickAddChild,
  includeInactive = false,
  onToggleIncludeInactive,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedMap, setCollapsedMap] = useState({});

  // Modals state
  const [editingNode, setEditingNode] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [deactivatingNode, setDeactivatingNode] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const toggleCollapse = (uid) => {
    setCollapsedMap((prev) => ({ ...prev, [uid]: !prev[uid] }));
  };

  const handleExpandAll = () => {
    setCollapsedMap({});
  };

  const handleCollapseAll = () => {
    const map = {};
    nodes.forEach((n) => {
      map[n.uid] = true;
    });
    setCollapsedMap(map);
  };

  // Count descendants of a node
  const getDescendantCount = (nodeId) => {
    let count = 0;
    const stack = [nodeId];
    while (stack.length > 0) {
      const curr = stack.pop();
      const children = nodes.filter((n) => n.parentId === curr);
      count += children.length;
      children.forEach((c) => stack.push(c.id));
    }
    return count;
  };

  // Handle Edit Submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editName.trim()) return;
    try {
      setSavingEdit(true);
      await serviceRequestTypeService.updateTreeNode(typeUid, editingNode.uid, {
        name: editName.trim(),
      });
      toastService.success('Option updated successfully');
      setEditingNode(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || 'Failed to update option'));
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle Deactivate Confirm
  const handleConfirmDeactivate = async () => {
    if (!deactivatingNode) return;
    try {
      setDeactivating(true);
      const res = await serviceRequestTypeService.deleteTreeNode(typeUid, deactivatingNode.uid);
      toastService.success(res.data?.message || 'Option deactivated successfully');
      setDeactivatingNode(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || 'Failed to deactivate option'));
    } finally {
      setDeactivating(false);
    }
  };

  // Handle Reactivate Option
  const handleReactivateNode = async (node) => {
    if (node.parentId) {
      const parent = nodes.find((n) => n.id === node.parentId);
      if (parent && !parent.isActive) {
        toastService.error(`Cannot reactivate "${node.name}" because its parent "${parent.name}" is inactive. Reactivate parent first.`);
        return;
      }
    }
    try {
      await serviceRequestTypeService.updateTreeNode(typeUid, node.uid, {
        isActive: true,
      });
      toastService.success(`"${node.name}" reactivated successfully`);
      if (onRefresh) onRefresh();
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || 'Failed to reactivate option'));
    }
  };

  // Client-side tree filtering for immediate responsiveness
  const filteredTree = useMemo(() => {
    if (includeInactive) return tree;
    const prune = (items) =>
      items
        .filter((item) => item.isActive)
        .map((item) => ({
          ...item,
          children: item.children ? prune(item.children) : [],
        }));
    return prune(tree);
  }, [tree, includeInactive]);

  return (
    <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-base-content/10">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-primary/10 text-primary">
            <ListTree size={20} />
          </div>
          <div>
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              Tree Hierarchy Explorer
            </h2>
            <p className="text-xs text-base-content/50">
              Interactive visualization of all options and nested child nodes ({nodes.length} total nodes).
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleExpandAll}
            className="px-2.5 py-1 text-xs font-bold rounded-lg border border-base-content/20 hover:bg-base-200 transition-colors"
          >
            Expand All
          </button>
          <button
            type="button"
            onClick={handleCollapseAll}
            className="px-2.5 py-1 text-xs font-bold rounded-lg border border-base-content/20 hover:bg-base-200 transition-colors"
          >
            Collapse All
          </button>
          {onToggleIncludeInactive && (
            <button
              type="button"
              onClick={onToggleIncludeInactive}
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg border transition-all ${
                includeInactive
                  ? 'bg-amber-500/15 border-amber-500/40 text-amber-700 dark:text-amber-400 hover:bg-amber-500/25 shadow-xs'
                  : 'border-base-content/20 text-base-content/70 hover:bg-base-200'
              }`}
              title={includeInactive ? 'Click to hide inactive options' : 'Click to show inactive options'}
            >
              {includeInactive ? (
                <>
                  <Eye size={13} className="text-amber-600 dark:text-amber-400" />
                  <span>Showing Inactive</span>
                </>
              ) : (
                <>
                  <EyeOff size={13} />
                  <span>Show Inactive</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40"
        />
        <input
          type="text"
          placeholder="Filter or search nodes by name..."
          className="w-full pl-9 pr-4 py-2 bg-base-100 border border-base-300 rounded-xl text-xs text-base-content focus:border-primary focus:outline-none placeholder:text-base-content/30"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Tree Content */}
      <div className="space-y-1 pt-1 min-h-[160px]">
        {filteredTree.length === 0 ? (
          <div className="p-8 text-center rounded-xl bg-base-200/30 border border-dashed border-base-content/10 space-y-2">
            <ListTree size={28} className="mx-auto text-base-content/30" />
            <p className="text-xs font-bold text-base-content/60">
              {tree.length > 0 && !includeInactive
                ? 'All options in this tree are currently inactive.'
                : 'No options have been added to this tree hierarchy yet.'}
            </p>
            <p className="text-[11px] text-base-content/40">
              {tree.length > 0 && !includeInactive ? (
                <button
                  type="button"
                  onClick={onToggleIncludeInactive}
                  className="text-primary hover:underline font-bold inline-flex items-center gap-1"
                >
                  <Eye size={12} /> Click here to show inactive options
                </button>
              ) : (
                `Use the Guided Option Adder above to start adding Level 1 (${levels[0]?.name || 'Category'}) options.`
              )}
            </p>
          </div>
        ) : (
          filteredTree.map((rootNode) => (
            <TreeNodeItem
              key={rootNode.uid}
              node={rootNode}
              isParentActive={true}
              levels={levels}
              onQuickAddChild={onQuickAddChild}
              onEditNode={(node) => {
                setEditingNode(node);
                setEditName(node.name);
              }}
              onDeleteNode={(node) => setDeactivatingNode(node)}
              onReactivateNode={handleReactivateNode}
              collapsedMap={collapsedMap}
              toggleCollapse={toggleCollapse}
              searchQuery={searchQuery}
            />
          ))
        )}
      </div>

      {/* Edit Option Modal */}
      {editingNode && (
        <Modal
          isOpen={!!editingNode}
          onClose={() => setEditingNode(null)}
          title="Edit Option"
        >
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-base-content/70">
                Option Name ({levels[editingNode.depth]?.name || 'Option'})
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-base-100 border border-base-300 rounded-xl text-sm text-base-content focus:border-primary focus:outline-none"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditingNode(null)}
                className="px-4 py-2 border border-base-content/20 text-xs font-bold rounded-xl hover:bg-base-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingEdit || !editName.trim()}
                className="btn-premium px-4 py-2 text-xs font-bold"
              >
                {savingEdit ? 'Saving...' : 'Save Name'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Deactivate Option Modal */}
      {deactivatingNode && (
        <Modal
          isOpen={!!deactivatingNode}
          onClose={() => setDeactivatingNode(null)}
          title="Deactivate Option"
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3.5 rounded-xl bg-warning/10 border border-warning/20 text-warning">
              <AlertTriangle size={20} className="shrink-0 mt-0.5" />
              <div className="text-xs space-y-1 text-base-content">
                <p className="font-bold">
                  Are you sure you want to deactivate "{deactivatingNode.name}"?
                </p>
                {getDescendantCount(deactivatingNode.id) > 0 && (
                  <p className="text-error font-medium">
                    This option has <strong>{getDescendantCount(deactivatingNode.id)} child descendant(s)</strong> that will also be automatically deactivated.
                  </p>
                )}
                <p className="text-base-content/60">
                  Deactivated options will be hidden from new {SERVICE_REQUEST_LABEL.singularLower} pickers, but existing {SERVICE_REQUEST_LABEL.pluralLower} will continue to preserve their historical data.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeactivatingNode(null)}
                className="px-4 py-2 border border-base-content/20 text-xs font-bold rounded-xl hover:bg-base-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deactivating}
                onClick={handleConfirmDeactivate}
                className="px-4 py-2 bg-error text-error-content hover:bg-error/90 text-xs font-bold rounded-xl transition-all"
              >
                {deactivating ? 'Deactivating...' : 'Yes, Deactivate Option & Subtree'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

HierarchyTreeView.propTypes = {
  typeUid: PropTypes.string.isRequired,
  levels: PropTypes.array,
  tree: PropTypes.array,
  nodes: PropTypes.array,
  onRefresh: PropTypes.func,
  onQuickAddChild: PropTypes.func,
  includeInactive: PropTypes.bool,
  onToggleIncludeInactive: PropTypes.func,
};

export default HierarchyTreeView;
